/**
 * @poc-to-production
 *
 * CodeSourcePanel
 *
 * Renders the engine-side provenance for a violation: cited code paragraph,
 * threshold span, observed-vs-threshold comparison, exceptions considered,
 * extraction provenance. The point of this panel is honesty — the architect
 * sees exactly what Vera read, what Vera measured, and what Vera could not
 * decide.
 *
 * History: a hand-authored "mock" branch lived here until 2026-05-17 to
 * cover code categories the engine had not yet extracted. It was removed
 * once stair / window / railing extractors landed; every panel now reads
 * a real engine result.
 */
import type { RuleSpec } from "@vera/rules";
import type { Violation, ViolationProvenance } from "@vera/shared";
import { useMemo } from "preact/hooks";
import {
  evaluatePredicateChecks,
  formatCheckLine,
  type PredicateCheck,
  ruleVerdictFromChecks,
} from "../lib/rulePredicateChecks";
import { latestUserFactsForElementRule } from "../lib/userFactAuditDb";
import { overridesForRule } from "../lib/userFactScope";
import { useEngineStore } from "../stores/engineStore";
import { useFactPreviewStore } from "../stores/factPreviewStore";
import { useProjectStore } from "../stores/projectStore";
import { useUiModeStore } from "../stores/uiModeStore";
import {
  mergeElementWithUserFacts,
  useUserFactStore,
} from "../stores/userFactStore";

interface Props {
  violation: Violation;
  rule?: RuleSpec;
}

export function CodeSourcePanel({ violation, rule }: Props) {
  const prov = violation.provenance;
  if (!prov) return null;
  return (
    <EngineProvenancePanel
      provenance={prov}
      violation={violation}
      rule={rule}
    />
  );
}

function EngineProvenancePanel({
  provenance,
  violation,
  rule,
}: {
  provenance: Extract<ViolationProvenance, { source: "engine" }>;
  violation: Violation;
  rule?: RuleSpec;
}) {
  const cite = provenance.cite;
  const observed = provenance.observed;
  const threshold = provenance.threshold;
  const status = provenance.status ?? "confirmed_violation";
  const indeterminateExceptions =
    provenance.exceptionsConsidered?.filter(
      e => e.decision === "indeterminate"
    ) ?? [];
  const technical = useUiModeStore(s => s.mode) === "technical";

  const predicateChecks = usePredicateChecks(violation, rule);
  const verdict =
    rule && predicateChecks.length > 0
      ? ruleVerdictFromChecks(predicateChecks, {
          ruleId: rule.ruleId,
          dbId: violation.elementDbId ?? 0,
          status: provenance.status ?? "indeterminate",
          summary: violation.summary,
        } as Parameters<typeof ruleVerdictFromChecks>[1])
      : null;

  const verdictColor =
    verdict?.tone === "fail"
      ? "#e87070"
      : verdict?.tone === "pass"
        ? "#22cc66"
        : verdict?.tone === "review"
          ? "#e5b85f"
          : "var(--vera-text-dim)";

  return (
    <div style={{ marginTop: 20 }}>
      {verdict && (
        <div
          style={{
            fontSize: 12,
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${verdictColor}`,
            borderRadius: 6,
            padding: "8px 10px",
            marginBottom: 12,
            lineHeight: 1.45,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              color: verdictColor,
              marginBottom: 6,
              fontSize: 13,
            }}
          >
            {verdict.headline}
          </div>
          {predicateChecks.map(c => (
            <div
              key={c.factKey}
              style={{
                color:
                  c.status === "fail"
                    ? "#e87070"
                    : c.status === "pass"
                      ? "var(--vera-text)"
                      : "var(--vera-text-dim)",
              }}
            >
              {formatCheckLine(c)}
            </div>
          ))}
        </div>
      )}

      {/* Status header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <StatusBadge status={status} technical={technical} />
        {technical && <ExtractionBadge extraction={provenance.extraction} />}
        {technical && <ConfidenceBadge confidence={provenance.confidence} />}
        {cite?.jurisdictionDisplay && (
          <span
            style={{
              background: "rgba(127,179,213,0.18)",
              color: "#7fb3d5",
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              padding: "3px 8px",
              borderRadius: 3,
              fontWeight: 600,
            }}
            title={`Jurisdiction id: ${cite.jurisdictionId ?? "?"}`}
          >
            {cite.jurisdictionDisplay}
          </span>
        )}
      </div>

      {/* Observed vs threshold (single-predicate rules) */}
      {observed && threshold && predicateChecks.length <= 1 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <Cell label="Measured" value={`${observed.value} ${observed.unit}`} />
          <Cell
            label={`Required (${status === "confirmed_violation" ? "violated" : "applied"})`}
            value={`${threshold.value} ${threshold.unit}`}
          />
        </div>
      )}

      {/* Cited code paragraph */}
      {cite && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              color: "var(--vera-text-dim)",
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            Source — {cite.code} §{cite.section}
          </div>
          <blockquote
            style={{
              margin: 0,
              padding: "10px 12px",
              borderLeft: "3px solid var(--vera-violation)",
              background: "rgba(255,255,255,0.03)",
              fontSize: 12,
              lineHeight: 1.5,
              borderRadius: 2,
            }}
          >
            {renderHighlightedVerbatim(cite.verbatim, cite.thresholdSpan)}
          </blockquote>
          <div style={{ marginTop: 6, fontSize: 11 }}>
            <a
              href={cite.canonicalUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--vera-text-dim)" }}
            >
              View canonical source
            </a>
            <span style={{ color: "var(--vera-text-dim)" }}>
              {" · "}repo: <code>{cite.sourceFile}</code>
            </span>
          </div>
        </div>
      )}

      {/* Family-1 validator findings (ADR 0007) — shown when the rule
          didn't pass cleanly. Filtered to errors + warnings; info
          findings are intentionally hidden to keep the panel scannable.
          Hidden in end-user mode: this is internal QA information that
          a customer doesn't need. */}
      {technical && <ValidatorFindings confidence={provenance.confidence} />}

      {/* Indeterminate exceptions — what's missing */}
      {indeterminateExceptions.length > 0 && (
        <div
          style={{
            marginBottom: 14,
            padding: "8px 10px",
            background: "rgba(229,184,95,0.08)",
            borderLeft: "3px solid #e5b85f",
            borderRadius: 2,
            fontSize: 11,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            What Vera could not decide ({indeterminateExceptions.length}{" "}
            exception
            {indeterminateExceptions.length === 1 ? "" : "s"})
          </div>
          <div style={{ color: "var(--vera-text-dim)", lineHeight: 1.45 }}>
            The model lacks facts needed to evaluate{" "}
            {indeterminateExceptions.length === 1
              ? "this exception"
              : "these exceptions"}{" "}
            (e.g. occupancy classification, whether the door is on the required
            means of egress). Once those facts are authored or supplied via AEC
            DM the result may change.
          </div>
          <ul
            style={{
              margin: "6px 0 0",
              paddingLeft: 18,
              fontSize: 11,
              color: "var(--vera-text-dim)",
            }}
          >
            {indeterminateExceptions.map(ex => (
              <li key={ex.exceptionId}>
                <code>{ex.exceptionId}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Provenance footer — rule ID + run ID. Only shown in
          technical mode; an end-user has no use for these. */}
      {technical && (
        <div
          style={{
            fontSize: 10,
            color: "var(--vera-text-dim)",
            opacity: 0.8,
            marginTop: 12,
          }}
        >
          rule <code>{provenance.ruleId}</code>
          <br />
          run <code>{provenance.runId}</code>
        </div>
      )}
    </div>
  );
}

/**
 * End-user mode uses outcome-oriented language ("Violation",
 * "Needs more information"); technical mode keeps the engine's
 * precise terminology ("Confirmed violation", "Indeterminate") so
 * developers reading the source panel can correlate with
 * `evaluateRule` status values.
 */
function StatusBadge({
  status,
  technical,
}: {
  status: string;
  technical: boolean;
}) {
  const palette: Record<
    string,
    { bg: string; fg: string; technical: string; endUser: string }
  > = {
    confirmed_violation: {
      bg: "rgba(214,83,83,0.18)",
      fg: "var(--vera-violation)",
      technical: "Confirmed violation",
      endUser: "Violation",
    },
    indeterminate: {
      bg: "rgba(229,184,95,0.18)",
      fg: "#e5b85f",
      technical: "Indeterminate",
      endUser: "Needs more information",
    },
    compliant_via_exception: {
      bg: "rgba(127,179,213,0.18)",
      fg: "#7fb3d5",
      technical: "Compliant via exception",
      endUser: "Allowed by exception",
    },
    compliant: {
      bg: "rgba(120,200,150,0.18)",
      fg: "#78c896",
      technical: "Compliant",
      endUser: "Compliant",
    },
  };
  const p = palette[status] ?? palette.confirmed_violation;
  return (
    <span
      style={{
        background: p.bg,
        color: p.fg,
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        padding: "3px 8px",
        borderRadius: 3,
        fontWeight: 600,
      }}
    >
      {technical ? p.technical : p.endUser}
    </span>
  );
}

/**
 * Family-1 verdict badge per ADR 0007. Shown next to the extraction
 * badge so the reviewer sees at a glance whether the validator
 * approved this rule.
 */
function ConfidenceBadge({
  confidence,
}: {
  confidence?: ViolationProvenance["confidence"];
}) {
  const tier = confidence?.tier ?? "unvalidated";
  const palette: Record<string, { bg: string; fg: string; label: string }> = {
    high: {
      bg: "rgba(120,200,150,0.18)",
      fg: "#78c896",
      label: "Validator: high",
    },
    medium: {
      bg: "rgba(229,184,95,0.18)",
      fg: "#e5b85f",
      label: "Validator: needs review",
    },
    low: {
      bg: "rgba(229,184,95,0.18)",
      fg: "#e5b85f",
      label: "Validator: low",
    },
    rejected: {
      bg: "rgba(214,83,83,0.18)",
      fg: "var(--vera-violation)",
      label: "Validator: quarantined",
    },
    unvalidated: {
      bg: "rgba(255,255,255,0.06)",
      fg: "var(--vera-text-dim)",
      label: "Validator: unrun",
    },
  };
  const p = palette[tier] ?? palette.unvalidated;
  const findingsCount = confidence?.findings?.length ?? 0;
  const title = confidence
    ? `Family-1 (${confidence.familyVersion}) validated ${new Date(confidence.validatedAt).toLocaleString()} — ${findingsCount} finding(s)`
    : "Family-1 validator has not run on this rule yet";
  return (
    <span
      style={{
        background: p.bg,
        color: p.fg,
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        padding: "3px 8px",
        borderRadius: 3,
        fontWeight: 600,
      }}
      title={title}
    >
      {p.label}
    </span>
  );
}

/**
 * Filtered list of Family-1 findings. Only shown when the validator
 * surfaced something the reviewer should act on (errors + warnings).
 */
function ValidatorFindings({
  confidence,
}: {
  confidence?: ViolationProvenance["confidence"];
}) {
  if (!confidence) return null;
  const actionable = confidence.findings.filter(
    f => f.severity === "error" || f.severity === "warning"
  );
  if (actionable.length === 0) return null;
  return (
    <div
      style={{
        marginBottom: 14,
        padding: "8px 10px",
        background: "rgba(229,184,95,0.08)",
        borderLeft: "3px solid #e5b85f",
        borderRadius: 2,
        fontSize: 11,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Validator findings ({actionable.length}) — ADR 0007 Family 1
      </div>
      <ul
        style={{
          margin: "4px 0 0",
          paddingLeft: 18,
          color: "var(--vera-text-dim)",
          lineHeight: 1.45,
        }}
      >
        {actionable.map((f, i) => (
          <li key={`${f.check}-${i}`}>
            <code style={{ fontSize: 10 }}>{f.check}</code>: {f.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function usePredicateChecks(
  violation: Violation,
  rule?: RuleSpec
): PredicateCheck[] {
  const elements = useEngineStore(s => s.elements);
  const allOverrides = useUserFactStore(s => s.overrides);
  const projectId = useProjectStore(s => s.currentProjectId);
  const previewId = useFactPreviewStore(s => s.violationId);
  const previewFacts = useFactPreviewStore(s => s.previewFacts);

  return useMemo(() => {
    if (!rule || typeof violation.elementDbId !== "number") return [];
    const el = elements.find(e => e.dbId === violation.elementDbId);
    if (!el) return [];
    const facts =
      previewId === violation.id && previewFacts
        ? previewFacts
        : mergeElementWithUserFacts(el, {
            [el.dbId]: overridesForRule(
              el.dbId,
              rule.ruleId,
              allOverrides,
              latestUserFactsForElementRule(projectId, el.dbId, rule.ruleId)
            ),
          }).facts;
    return evaluatePredicateChecks(rule, facts);
  }, [
    rule,
    violation.id,
    violation.elementDbId,
    elements,
    allOverrides,
    projectId,
    previewId,
    previewFacts,
  ]);
}

function ExtractionBadge({
  extraction,
}: {
  extraction?: { method: string; provider?: string; model?: string };
}) {
  if (!extraction) return null;
  const label =
    extraction.method === "llm"
      ? `LLM extraction (${extraction.provider ?? "?"}/${extraction.model ?? "?"})`
      : "Hand-curated rule";
  return (
    <span
      style={{
        background: "rgba(255,255,255,0.06)",
        color: "var(--vera-text-dim)",
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        padding: "3px 8px",
        borderRadius: 3,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "rgba(255,255,255,0.04)",
        borderRadius: 3,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          color: "var(--vera-text-dim)",
          letterSpacing: 0.5,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

/**
 * Render the verbatim source paragraph with the threshold-bearing span
 * highlighted, so the architect can see at a glance which sentence
 * established the rule.
 */
function renderHighlightedVerbatim(verbatim: string, span: string) {
  if (!span || !verbatim.includes(span)) {
    return <>{verbatim}</>;
  }
  const idx = verbatim.indexOf(span);
  const before = verbatim.slice(0, idx);
  const after = verbatim.slice(idx + span.length);
  return (
    <>
      {before}
      <mark
        style={{
          background: "rgba(214,83,83,0.25)",
          color: "inherit",
          padding: "0 2px",
          borderRadius: 2,
        }}
      >
        {span}
      </mark>
      {after}
    </>
  );
}
